'use client'

import {useState, useRef, useId, useEffect} from 'react'
import {useRouter} from 'next/navigation'
import HgBlogSearchInput from '~/design-system/hg/components/HgBlogSearchInput'
import {internalLinks} from '~/routing/internal-links'
import useQueryStringGenerator from '~/design-system/hooks/useQueryStringGenerator'

type IndexPageSearchInputProps = {
  initialSearchValue: string
}

export default function IndexPageSearchInput({
  initialSearchValue,
}: IndexPageSearchInputProps) {
  const [searchValue, setSearchValue] = useState(initialSearchValue)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const router = useRouter()
  const inputId = useId()
  const queryStringGenerator = useQueryStringGenerator()

  // Focus input on initial render
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    setSearchValue(initialSearchValue)
  }, [initialSearchValue])

  const handleClearSearch = () => {
    setSearchValue('')

    // clear query param and reset pagination
    const newUrl = queryStringGenerator(
      {query: '', page: ''},
      internalLinks.blogIndex
    )

    router.push(newUrl)

    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    })
  }

  return (
    <HgBlogSearchInput
      buttonLinkProps={{
        'href': queryStringGenerator(
          {query: searchValue.trim(), page: ''},
          internalLinks.blogIndex
        ),
        'variant': 'text',
        'contentType': 'icon',
        'iconProps': {iconType: 'search'},
        'size': 'xl',
        'aria-label': 'Search',
      }}
      id={inputId}
      ref={inputRef}
      placeholder="Search"
      value={searchValue}
      onChange={e => {
        setSearchValue(e.target.value)
      }}
      onClear={handleClearSearch}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          router.push(
            queryStringGenerator(
              {query: searchValue.trim(), page: ''},
              internalLinks.blogIndex
            )
          )
        }
      }}
    />
  )
}
